import { useState } from "react";
import {
  BarChart,
  DataRow,
  DataTable,
  LineChart,
  MetricCard,
  ProfileTabs,
  Skeleton,
} from "@breakingthelines/design-system";
import { Link } from "react-router-dom";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import {
  useGoogleAnalytics,
  useJudgeVerdictMix,
  usePublishingAnalytics,
  useSearchConsole,
  useTopContentAnalytics,
  useTrafficAnalytics,
} from "@/hooks/use-analytics";
import type { AnalyticsRange } from "@/lib/admin-api";
import {
  ANALYTICS_RANGES,
  bucketForRange,
  formatBucketLabel,
  formatCount,
  formatPercent,
  formatPosition,
  formatReadAge,
  RANGE_LABEL,
  searchPagePath,
  topContentPrimaryLabel,
} from "@/lib/analytics";

import styles from "./analytics-overview.module.css";

// Analytics.
//
// Three sources, three different populations: our own reader events, Google
// Analytics, and Google Search Console. Nothing here reconciles one against
// another.
//
// Every section fetches on its own and degrades on its own: a section that
// cannot load shows one line where its figures would be, and the others carry
// on. Empty is a different state from failed, and each section says which.

const RANGE_TABS = ANALYTICS_RANGES.map((range) => RANGE_LABEL[range]);

const RANGE_BY_TAB = new Map<string, AnalyticsRange>(
  ANALYTICS_RANGES.map((range) => [RANGE_LABEL[range], range]),
);

const TOP_CONTENT_LIMIT = 10;

/** Chart axis labels: first, middle and last bucket. */
// Bar labels at most ~8 across, evenly spaced and always including the last
// bar, so a 30- or 90-bucket window stays legible. Short windows keep every label.
const MAX_BAR_LABELS = 8;
function sparseBarLabels(buckets: string[], counts: number[]): { top: string; bottom: string }[] {
  const step = Math.max(1, Math.ceil(buckets.length / MAX_BAR_LABELS));
  return buckets.map((bucket, index) => {
    const shown = index % step === 0 || index === buckets.length - 1;
    return shown
      ? { top: formatCount(counts[index]), bottom: formatBucketLabel(bucket) }
      : { top: "", bottom: "" };
  });
}

function axisLabels(buckets: string[]): string[] {
  if (buckets.length === 0) return [];
  const indices = [0, Math.floor((buckets.length - 1) / 2), buckets.length - 1];
  const labels = [...new Set(indices)].map((index) => formatBucketLabel(buckets[index]));
  return labels.filter((label, index) => label !== "" && labels.indexOf(label) === index);
}

function SectionUnavailable({ children }: { children: string }) {
  return <p className={styles.unavailable}>{children}</p>;
}

/*
 * A loading section here swaps its whole body out, so a spinner centred in the
 * gap collapses the layout and jolts it back when the figures arrive. These
 * hold the shape the section is about to take instead. `role="status"` keeps
 * the announcement the spinner made, under the same label.
 */
function LoadingCards({ label }: { label: string }) {
  return (
    <div className={styles.loading} role="status" aria-live="polite" aria-label={label}>
      <div className={styles.cards}>
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <div className={styles.charts}>
        <Skeleton className="h-44" />
        <Skeleton className="h-44" />
      </div>
    </div>
  );
}

function LoadingRows({ label, rows }: { label: string; rows: number }) {
  return (
    <div className={styles.loading} role="status" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9" />
      ))}
    </div>
  );
}

export function AnalyticsOverviewPage() {
  const [tab, setTab] = useState(RANGE_TABS[1]);
  const range = RANGE_BY_TAB.get(tab) ?? "30d";
  const bucket = bucketForRange(range);

  const traffic = useTrafficAnalytics(range);
  const google = useGoogleAnalytics(range);
  const search = useSearchConsole(range);
  const topContent = useTopContentAnalytics(range, TOP_CONTENT_LIMIT);
  const publishing = usePublishingAnalytics(range);
  const verdicts = useJudgeVerdictMix();

  const trafficPoints = traffic.data?.points ?? [];
  const publishingPoints = publishing.data?.points ?? [];

  const googleReadAge = formatReadAge(google.data?.cachedAt, new Date());
  const searchReadAge = formatReadAge(search.data?.cachedAt, new Date());

  const totalViews = trafficPoints.reduce((sum, point) => sum + point.views, 0);
  const peakReaders = trafficPoints.reduce((peak, point) => Math.max(peak, point.uniqueReaders), 0);
  const totalPublished = publishingPoints.reduce((sum, point) => sum + point.publishedCount, 0);

  const bucketNoun = bucket === "week" ? "week" : "day";

  const googleEmpty =
    (google.data?.topSources.length ?? 0) === 0 &&
    (google.data?.topCountries.length ?? 0) === 0 &&
    (google.data?.ageBrackets.length ?? 0) === 0 &&
    (google.data?.genders.length ?? 0) === 0;

  const searchEmpty = (search.data?.clicks ?? 0) === 0 && (search.data?.impressions ?? 0) === 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Analytics</h1>
      </div>

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Reporting window"
          tabs={RANGE_TABS.map((item) => ({ id: item, label: item }))}
          activeTab={tab}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={setTab}
        />
        <span className={styles.window}>
          {bucket === "week" ? "By week, Monday to Monday, UTC" : "By day, UTC"}
        </span>
      </div>

      {/* --- Audience --- */}
      <section className={styles.section} aria-labelledby="analytics-audience">
        <h2 className={styles.sectionTitle} id="analytics-audience">
          Audience
        </h2>

        {traffic.loading ? <LoadingCards label="Loading audience..." /> : null}

        {!traffic.loading && traffic.failed ? (
          <SectionUnavailable>Audience figures are unavailable.</SectionUnavailable>
        ) : null}

        {!traffic.loading && !traffic.failed ? (
          trafficPoints.length === 0 ? (
            <p className={styles.empty}>No reader events in this window.</p>
          ) : (
            <>
              <div className={styles.cards}>
                <MetricCard
                  label="Views"
                  value={formatCount(totalViews)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Article view starts"
                />
                <MetricCard
                  label={`Peak readers in a ${bucketNoun}`}
                  value={formatCount(peakReaders)}
                  delta={`busiest ${bucketNoun}`}
                  hint={`Distinct readers, counted per ${bucketNoun}`}
                />
              </div>

              <div className={styles.charts}>
                <figure className={styles.chartBlock}>
                  <figcaption className={styles.chartCaption}>Views per {bucketNoun}</figcaption>
                  <LineChart
                    series={trafficPoints.map((point) => point.views)}
                    xLabels={axisLabels(trafficPoints.map((point) => point.bucketStart))}
                  />
                </figure>
                <figure className={styles.chartBlock}>
                  <figcaption className={styles.chartCaption}>Readers per {bucketNoun}</figcaption>
                  <LineChart
                    series={trafficPoints.map((point) => point.uniqueReaders)}
                    xLabels={axisLabels(trafficPoints.map((point) => point.bucketStart))}
                  />
                </figure>
              </div>
            </>
          )
        ) : null}

        {/* Secondary breakdowns Google Analytics has and our own reader
            events do not: traffic sources, countries, demographics. */}
        <p className={styles.groupLabel}>From Google Analytics</p>

        {google.loading ? <LoadingRows label="Loading Google Analytics..." rows={3} /> : null}

        {!google.loading && google.failed ? (
          <SectionUnavailable>Traffic sources and countries are unavailable.</SectionUnavailable>
        ) : null}

        {!google.loading && !google.failed && google.data?.configured === false ? (
          <SectionUnavailable>Google Analytics is not connected.</SectionUnavailable>
        ) : null}

        {!google.loading && !google.failed && google.data?.configured ? (
          googleEmpty ? (
            <p className={styles.empty}>No sessions in this window.</p>
          ) : (
            <>
              <div className={styles.groups}>
                <div className={styles.group}>
                  <p className={styles.groupLabel}>Top sources</p>
                  {google.data.topSources.length === 0 ? (
                    <p className={styles.empty}>No sources in this window.</p>
                  ) : (
                    <DataTable
                      columns="minmax(200px, 2.4fr) minmax(100px, 0.7fr)"
                      header={["Source", "Sessions"]}
                      headerClassName={styles.tableHeader}
                    >
                      {google.data.topSources.map((source) => (
                        <DataRow key={source.source} className={styles.tableRow}>
                          <span className={styles.strong} data-label="Source">
                            {source.source}
                          </span>
                          <span className={styles.meta} data-label="Sessions">
                            {formatCount(source.sessions)}
                          </span>
                        </DataRow>
                      ))}
                    </DataTable>
                  )}
                </div>

                <div className={styles.group}>
                  <p className={styles.groupLabel}>Top countries</p>
                  {google.data.topCountries.length === 0 ? (
                    <p className={styles.empty}>No countries in this window.</p>
                  ) : (
                    <DataTable
                      columns="minmax(200px, 2.4fr) minmax(100px, 0.7fr)"
                      header={["Country", "Users"]}
                      headerClassName={styles.tableHeader}
                    >
                      {google.data.topCountries.map((country) => (
                        <DataRow key={country.country} className={styles.tableRow}>
                          <span className={styles.strong} data-label="Country">
                            {country.country}
                          </span>
                          <span className={styles.meta} data-label="Users">
                            {formatCount(country.users)}
                          </span>
                        </DataRow>
                      ))}
                    </DataTable>
                  )}
                </div>

                <div className={styles.group}>
                  <p className={styles.groupLabel}>Age and gender</p>
                  {google.data.ageBrackets.length === 0 && google.data.genders.length === 0 ? (
                    <p className={styles.empty}>Not reported for this property.</p>
                  ) : (
                    <DataTable
                      columns="minmax(200px, 2.4fr) minmax(100px, 0.7fr)"
                      header={["Segment", "Users"]}
                      headerClassName={styles.tableHeader}
                    >
                      {[
                        ...google.data.ageBrackets.map((segment) => (
                          <DataRow key={`age-${segment.segment}`} className={styles.tableRow}>
                            <span className={styles.strong} data-label="Segment">
                              {segment.segment}
                            </span>
                            <span className={styles.meta} data-label="Users">
                              {formatCount(segment.users)}
                            </span>
                          </DataRow>
                        )),
                        ...google.data.genders.map((segment) => (
                          <DataRow key={`gender-${segment.segment}`} className={styles.tableRow}>
                            <span className={styles.strong} data-label="Segment">
                              {segment.segment}
                            </span>
                            <span className={styles.meta} data-label="Users">
                              {formatCount(segment.users)}
                            </span>
                          </DataRow>
                        )),
                      ]}
                    </DataTable>
                  )}
                </div>
              </div>

              {googleReadAge ? <p className={styles.note}>{googleReadAge}</p> : null}
            </>
          )
        ) : null}
      </section>

      {/* --- Search --- */}
      <section className={styles.section} aria-labelledby="analytics-search">
        <h2 className={styles.sectionTitle} id="analytics-search">
          Search
        </h2>

        {search.loading ? <LoadingCards label="Loading search..." /> : null}

        {!search.loading && search.failed ? (
          <SectionUnavailable>Search figures are unavailable.</SectionUnavailable>
        ) : null}

        {!search.loading && !search.failed && search.data?.configured === false ? (
          <SectionUnavailable>Search Console is not connected.</SectionUnavailable>
        ) : null}

        {!search.loading && !search.failed && search.data?.configured ? (
          searchEmpty ? (
            <p className={styles.empty}>No impressions in this window.</p>
          ) : (
            <>
              <div className={styles.cards}>
                <MetricCard
                  label="Clicks"
                  value={formatCount(search.data.clicks)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Clicks from a Google search result"
                />
                <MetricCard
                  label="Impressions"
                  value={formatCount(search.data.impressions)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Times a result appeared"
                />
                <MetricCard
                  label="CTR"
                  value={formatPercent(search.data.ctr)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Clicks per impression"
                />
                <MetricCard
                  label="Average position"
                  value={formatPosition(search.data.averagePosition)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Lower is better"
                />
              </div>

              <div className={styles.charts}>
                <figure className={styles.chartBlock}>
                  <figcaption className={styles.chartCaption}>Clicks per day</figcaption>
                  <LineChart
                    series={search.data.trend.map((point) => point.clicks)}
                    xLabels={axisLabels(search.data.trend.map((point) => point.date))}
                  />
                </figure>
              </div>

              <div className={styles.groups}>
                <div className={styles.group}>
                  <p className={styles.groupLabel}>Top queries</p>
                  {search.data.topQueries.length === 0 ? (
                    <p className={styles.empty}>No queries in this window.</p>
                  ) : (
                    <DataTable
                      columns="minmax(180px, 2fr) minmax(70px, 0.6fr) minmax(90px, 0.7fr) minmax(60px, 0.5fr) minmax(70px, 0.5fr)"
                      header={["Query", "Clicks", "Impressions", "CTR", "Position"]}
                      headerClassName={styles.tableHeader}
                    >
                      {search.data.topQueries.map((query) => (
                        <DataRow key={query.query} className={styles.tableRow}>
                          <span className={styles.strong} data-label="Query">
                            {query.query}
                          </span>
                          <span className={styles.meta} data-label="Clicks">
                            {formatCount(query.clicks)}
                          </span>
                          <span className={styles.meta} data-label="Impressions">
                            {formatCount(query.impressions)}
                          </span>
                          <span className={styles.meta} data-label="CTR">
                            {formatPercent(query.ctr)}
                          </span>
                          <span className={styles.meta} data-label="Position">
                            {formatPosition(query.position)}
                          </span>
                        </DataRow>
                      ))}
                    </DataTable>
                  )}
                </div>

                <div className={styles.group}>
                  <p className={styles.groupLabel}>Top pages</p>
                  {search.data.topPages.length === 0 ? (
                    <p className={styles.empty}>No pages in this window.</p>
                  ) : (
                    <DataTable
                      columns="minmax(180px, 2fr) minmax(70px, 0.6fr) minmax(90px, 0.7fr) minmax(60px, 0.5fr) minmax(70px, 0.5fr)"
                      header={["Page", "Clicks", "Impressions", "CTR", "Position"]}
                      headerClassName={styles.tableHeader}
                    >
                      {search.data.topPages.map((page) => (
                        <DataRow key={page.page} className={styles.tableRow}>
                          <span className={styles.strong} data-label="Page">
                            {searchPagePath(page.page)}
                          </span>
                          <span className={styles.meta} data-label="Clicks">
                            {formatCount(page.clicks)}
                          </span>
                          <span className={styles.meta} data-label="Impressions">
                            {formatCount(page.impressions)}
                          </span>
                          <span className={styles.meta} data-label="CTR">
                            {formatPercent(page.ctr)}
                          </span>
                          <span className={styles.meta} data-label="Position">
                            {formatPosition(page.position)}
                          </span>
                        </DataRow>
                      ))}
                    </DataTable>
                  )}
                </div>
              </div>

              <div className={styles.group}>
                <p className={styles.groupLabel}>High ranking, few clicks</p>
                {search.data.underperforming.length === 0 ? (
                  <p className={styles.empty}>None in this window.</p>
                ) : (
                  <DataTable
                    columns="minmax(180px, 2fr) minmax(70px, 0.5fr) minmax(90px, 0.7fr) minmax(60px, 0.5fr) minmax(70px, 0.6fr)"
                    header={["Page", "Position", "Impressions", "CTR", "Typical CTR"]}
                    headerClassName={styles.tableHeader}
                  >
                    {search.data.underperforming.map((page) => (
                      <DataRow key={page.page} className={styles.tableRow}>
                        <span className={styles.strong} data-label="Page">
                          {searchPagePath(page.page)}
                        </span>
                        <span className={styles.meta} data-label="Position">
                          {formatPosition(page.position)}
                        </span>
                        <span className={styles.meta} data-label="Impressions">
                          {formatCount(page.impressions)}
                        </span>
                        <span className={styles.meta} data-label="CTR">
                          {formatPercent(page.ctr)}
                        </span>
                        <span className={styles.meta} data-label="Typical CTR">
                          {formatPercent(page.expectedCtr)}
                        </span>
                      </DataRow>
                    ))}
                  </DataTable>
                )}
              </div>

              <div className={styles.group}>
                <p className={styles.groupLabel}>Sitemap</p>
                {search.data.sitemaps.unavailable ? (
                  <SectionUnavailable>Sitemap status is unavailable.</SectionUnavailable>
                ) : (
                  <p className={styles.note}>
                    {formatCount(search.data.sitemaps.submitted)} URLs submitted across{" "}
                    {formatCount(search.data.sitemaps.sitemapCount)} sitemap
                    {search.data.sitemaps.sitemapCount === 1 ? "" : "s"}.
                    {search.data.sitemaps.pending > 0
                      ? ` ${formatCount(search.data.sitemaps.pending)} pending.`
                      : ""}
                    {search.data.sitemaps.errors > 0
                      ? ` ${formatCount(search.data.sitemaps.errors)} error${search.data.sitemaps.errors === 1 ? "" : "s"}.`
                      : ""}
                    {search.data.sitemaps.warnings > 0
                      ? ` ${formatCount(search.data.sitemaps.warnings)} warning${search.data.sitemaps.warnings === 1 ? "" : "s"}.`
                      : ""}
                  </p>
                )}
              </div>

              {searchReadAge ? <p className={styles.note}>{searchReadAge}</p> : null}
            </>
          )
        ) : null}
      </section>

      {/* --- Most read --- */}
      <section className={styles.section} aria-labelledby="analytics-top-content">
        <h2 className={styles.sectionTitle} id="analytics-top-content">
          Most read
        </h2>

        {topContent.loading ? <LoadingRows label="Loading most read..." rows={5} /> : null}

        {!topContent.loading && topContent.failed ? (
          <SectionUnavailable>Most read is unavailable.</SectionUnavailable>
        ) : null}

        {!topContent.loading && !topContent.failed ? (
          (topContent.data?.items.length ?? 0) === 0 ? (
            <p className={styles.empty}>Nothing was read in this window.</p>
          ) : (
            <>
              {topContent.data?.titlesUnavailable ? (
                <p className={styles.note}>
                  Titles are unavailable. Rows are identified by content id.
                </p>
              ) : null}
              <DataTable
                columns="minmax(240px, 2.4fr) minmax(100px, 0.7fr) minmax(110px, 0.8fr)"
                header={["Content", "Views", "Readers"]}
                headerClassName={styles.tableHeader}
              >
                {topContent.data?.items.map((item) => (
                  <DataRow key={item.entityId} className={styles.tableRow}>
                    <span className={styles.contentCell} data-label="Content">
                      <span className={styles.strong}>{topContentPrimaryLabel(item)}</span>
                      {item.title.trim() ? (
                        <span className={styles.subtle}>{item.entityId}</span>
                      ) : null}
                    </span>
                    <span className={styles.meta} data-label="Views">
                      {formatCount(item.views)}
                    </span>
                    <span className={styles.meta} data-label="Readers">
                      {formatCount(item.uniqueReaders)}
                    </span>
                  </DataRow>
                ))}
              </DataTable>
            </>
          )
        ) : null}
      </section>

      {/* --- Publishing --- */}
      <section className={styles.section} aria-labelledby="analytics-publishing">
        <h2 className={styles.sectionTitle} id="analytics-publishing">
          Publishing
        </h2>

        {publishing.loading ? <LoadingRows label="Loading publishing..." rows={4} /> : null}

        {!publishing.loading && publishing.failed ? (
          <SectionUnavailable>Publishing figures are unavailable.</SectionUnavailable>
        ) : null}

        {!publishing.loading && !publishing.failed ? (
          publishingPoints.length === 0 ? (
            <p className={styles.empty}>Nothing was published in this window.</p>
          ) : (
            <>
              <div className={styles.cards}>
                <MetricCard
                  label="Published"
                  value={formatCount(totalPublished)}
                  delta={`over ${RANGE_LABEL[range]}`}
                  hint="Every content type"
                />
              </div>
              <div className={styles.charts}>
                <figure className={styles.chartBlock}>
                  <figcaption className={styles.chartCaption}>
                    Published per {bucketNoun}
                  </figcaption>
                  <BarChart
                    bars={publishingPoints.map((point) => point.publishedCount)}
                    labels={sparseBarLabels(
                      publishingPoints.map((point) => point.bucketStart),
                      publishingPoints.map((point) => point.publishedCount),
                    )}
                  />
                </figure>
                <figure className={styles.chartBlock}>
                  <figcaption className={styles.chartCaption}>
                    Creators publishing per {bucketNoun}
                  </figcaption>
                  <BarChart
                    bars={publishingPoints.map((point) => point.distinctCreators)}
                    labels={sparseBarLabels(
                      publishingPoints.map((point) => point.bucketStart),
                      publishingPoints.map((point) => point.distinctCreators),
                    )}
                  />
                </figure>
              </div>
            </>
          )
        ) : null}
      </section>

      {/* --- Moderation --- */}
      <section className={styles.section} aria-labelledby="analytics-moderation">
        <h2 className={styles.sectionTitle} id="analytics-moderation">
          Moderation
        </h2>

        {verdicts.loading ? <LoadingRows label="Loading verdicts..." rows={3} /> : null}

        {!verdicts.loading && (verdicts.failed || !verdicts.data) ? (
          <SectionUnavailable>Verdict counts are unavailable.</SectionUnavailable>
        ) : null}

        {!verdicts.loading && !verdicts.failed && verdicts.data ? (
          <>
            <div className={styles.figures}>
              <div className={styles.figure}>
                <p className={styles.figureValue}>{formatCount(verdicts.data.clean)}</p>
                <p className={styles.figureMeta}>clean</p>
              </div>
              <div className={styles.figure}>
                <p className={styles.figureValue}>{formatCount(verdicts.data.spam)}</p>
                <p className={styles.figureMeta}>spam</p>
              </div>
              <div className={styles.figure}>
                <p className={styles.figureValue}>{formatCount(verdicts.data.other)}</p>
                <p className={styles.figureMeta}>other</p>
              </div>
            </div>
          </>
        ) : null}

        <div className={styles.linksRow}>
          <Link to="/moderation/queue">Moderation queue</Link>
          <Link to="/new-writers">New writers</Link>
        </div>
      </section>
    </div>
  );
}
